import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { config } from '@config/config'
import { connectorCartProductsSelector, connectorAfterCreateClaimRecord } from '@redux/slices/connectorSlice'
import { claimRecordIdSelector, setActiveClaimRecord, setInitiateClaimError } from '@redux/slices/claimStatusSlice'
import { useLazyGetCartItemsQuery } from '@redux/queries/cart'
import { useLazyGetCustomerContextQuery, useLazyGetSessionQuery } from '@redux/queries/sessions'
import {
  useCreateClaimRecordMutation,
  useLazyGetActiveClaimRecordQuery,
  useUpdateClaimRecordMutation,
} from '@redux/queries/claimRecords'
import { Session } from '@typesApp/Session'
import { ClaimData } from '@typesApp/ClaimData'
import { CartBundle } from '@typesApp/Product'
import { ClaimRecord, CreateClaimRecordPayload, CreateClaimRecordResult } from '@typesApp/ClaimRecord'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { createSessionExpiredFetchError } from '@utils/errorUtil'
import { isCustomerNumberValid } from '@utils/isCustomerNumberValid'
import { useEnvironment } from './useEnvironment'
import { useSessionInfo } from './useSessionInfo'

type CreateOptions = {
  insurerId?: string
}

type CreateClaimRecordParams = {
  session: Session
  options?: CreateOptions
}

type UseCreateClaimRecordReturn = {
  isError: boolean
  createClaimRecord: (options?: CreateOptions) => Promise<CreateClaimRecordResult> | never
  patchClaimRecord: (claimData: ClaimData, id?: string) => Promise<ClaimRecord>
}

export const useCreateClaimRecord = (): UseCreateClaimRecordReturn => {
  const dispatch = useDispatch()
  const { brand, ecommerceCode, ecommerceTransactionHost, lang, country } = useEnvironment()

  const claimRecordId = useSelector(claimRecordIdSelector)
  const cartProducts = useSelector(connectorCartProductsSelector)
  const { isOrderComplete } = useSessionInfo()

  const { performPrecheck } = usePrecheck()

  const [fetchCartItems] = useLazyGetCartItemsQuery()
  const [triggerCreate, { isError: isCreateError }] = useCreateClaimRecordMutation()
  const [triggerPatch, { isError: isUpdateError }] = useUpdateClaimRecordMutation()
  const [fetchActive] = useLazyGetActiveClaimRecordQuery()

  const isError = isCreateError || isUpdateError

  const getDefaultPayload = useCallback(
    (session: Session, options?: CreateOptions): CreateClaimRecordPayload => {
      const customer = session?.customer

      let payload: CreateClaimRecordPayload = {
        cartId: session?.cartId,
        sessionId: session?.sessionId,
        customerNumber: customer?.id,
        customerEmail: customer?.email,
        customerPhone: customer?.phoneNumber,
        customerName: {
          firstName: customer?.firstName,
          lastName: customer?.lastName,
        },
        eligibleProducts: cartProducts ?? [],
        locale: `${lang}_${country?.toUpperCase()}`,
        ecommerce: formatCommerceCode({
          brand,
          country,
          ecommerceCode,
          ecommerceTransactionHost,
        }),
      }

      // NOTE: remove cart id if order was just completed
      // this may happen if sync insurance is triggered
      // immmediately after order complete
      if (!session?.cartId || isOrderComplete) {
        delete payload.cartId
        payload.eligibleProducts = []
      }

      if (session?.isGuest) {
        delete payload.customerName
        delete payload.customerEmail
        delete payload.customerPhone
      }

      // do not send empty name
      const emptyName = !customer?.firstName && !customer?.lastName
      if (emptyName) {
        delete payload.customerName
      }

      if (options?.insurerId) {
        payload = {
          ...payload,
          claimData: {
            primaryCoverage: {
              insurer: {
                id: options?.insurerId ?? '',
              },
            },
          },
        }
      }

      return payload
    },
    [brand, ecommerceCode, ecommerceTransactionHost, lang, country, cartProducts, isOrderComplete]
  )

  const createClaimRecordDefault = useCallback(
    async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
      const { session, options } = params
      const payload = getDefaultPayload(session, options)
      const result = await triggerCreate(payload).unwrap()
      dispatch(connectorAfterCreateClaimRecord())
      return result
    },
    [dispatch, getDefaultPayload, triggerCreate]
  )

  const getPayloadFromCartItems = useCallback(
    (session: Session, cartBundle: CartBundle, options?: CreateOptions): CreateClaimRecordPayload => {
      const payload: CreateClaimRecordPayload = {
        ...getDefaultPayload(session, options),

        // Note: cartBundle.cartId may be undefined for
        // empty cart but commerce F/E provided a cartId
        cartId: cartBundle.cartId || session.cartId,
        eligibleProducts: cartBundle.products ?? [],
        orderSubtotal: cartBundle.orderSubtotal,
        shippingCosts: cartBundle.shippingCosts,
      }

      // empty string cartId not accepted on b/e
      if (!payload.cartId) {
        delete payload.cartId
      }

      return payload
    },
    [getDefaultPayload]
  )

  const createClaimRecordWithCartItems = useCallback(
    async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
      const { session, options } = params
      let cartBundle: CartBundle

      try {
        cartBundle = await fetchCartItems().unwrap()
      } catch (error) {
        // create claim record w/o cart products on cart items error
        return await createClaimRecordDefault(params)
      }

      const payload = getPayloadFromCartItems(session, cartBundle, options)
      const result = await triggerCreate(payload).unwrap()
      dispatch(connectorAfterCreateClaimRecord(cartBundle))
      return result
    },
    [dispatch, fetchCartItems, createClaimRecordDefault, getPayloadFromCartItems, triggerCreate]
  )

  const createNewClaimRecord = useCallback(
    async (params: CreateClaimRecordParams) => {
      // Always fetch latest cart products unless order was just completed.
      // Rare scenario - but if customer immediately creates new claim record
      // after completing an order, commerce may return the cart products
      // from the completed order.
      const useCartItems = !isOrderComplete
      if (useCartItems) {
        return createClaimRecordWithCartItems(params)
      } else {
        return createClaimRecordDefault(params)
      }
    },
    [createClaimRecordWithCartItems, createClaimRecordDefault, isOrderComplete]
  )

  /**
   * Create new claim record or return active one if exists
   */
  const createClaimRecord = useCallback(
    async (options?: CreateOptions): Promise<CreateClaimRecordResult> | never => {
      try {
        const session = await performPrecheck()

        // get active to ensure we do not create multiple active claim records for customer
        const activeData = await fetchActive({ customerNumber: session.customer.id }).unwrap()
        if (activeData?.id) {
          dispatch(setActiveClaimRecord(activeData.id))
          return { id: activeData.id }
        }

        console.debug(
          'directbilling - create claim record [sessionId=%s customerNumber=%s cartId=%s]',
          session.sessionId,
          session.customer?.id,
          session.cartId
        )

        return createNewClaimRecord({ session, options })
      } catch (error) {
        dispatch(setInitiateClaimError(true))
        throw error
      }
    },
    [dispatch, performPrecheck, fetchActive, createNewClaimRecord]
  )

  /**
   * Patch claim record - the provided id has precedence over the claimRecordId from redux state
   * The id parameter is useful when we need to create claim record and then immediately patch.
   */
  const patchClaimRecord = useCallback(
    (claimData: ClaimData, id?: string): Promise<ClaimRecord> => {
      return triggerPatch({
        id: id || claimRecordId,
        claimData,
      }).unwrap()
    },
    [triggerPatch, claimRecordId]
  )

  return {
    isError,
    createClaimRecord,
    patchClaimRecord,
  }
}

type UsePrecheckReturn = {
  performPrecheck: () => Promise<Session> | never
}

const usePrecheck = (): UsePrecheckReturn => {
  const { anonymousUserId } = useEnvironment()
  const { authTokenSessionId } = useSessionInfo()

  const [fetchSession] = useLazyGetSessionQuery()
  const [fetchCustomerContext] = useLazyGetCustomerContextQuery()

  const performPrecheck = useCallback(async (): Promise<Session> | never => {
    const disableSessionIdCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

    // always fetch latest session id
    const customerContext = await fetchCustomerContext().unwrap()
    if (!customerContext?.sessionId && !disableSessionIdCheck) {
      throw createSessionExpiredFetchError(
        'Got empty session id from commerce - cannot create claim record, session expired?'
      )
    }

    // always fetch latest customer number
    const session = await fetchSession(customerContext?.sessionId).unwrap()
    if (!session?.customer?.id || !isCustomerNumberValid(session?.customer?.id, anonymousUserId)) {
      throw createSessionExpiredFetchError(
        'Got invalid customer number from commerce - cannot create claim record, session expired?'
      )
    }

    // Note: use latest sessionId from customer context to ensure we do not
    // create claim record with an expired sessionId. This happens with
    // partially authenticated sessions where the sessionId has expired but
    // commerce session is still (partially) active.

    const latestSession = {
      ...session,
      ...customerContext,
    }

    // for brands where sessionId is optional, we use the
    // internal session id provided by authToken api
    if (disableSessionIdCheck) {
      latestSession.sessionId = authTokenSessionId ?? ''
    }

    return latestSession
  }, [fetchSession, fetchCustomerContext, anonymousUserId, authTokenSessionId])

  return {
    performPrecheck,
  }
}
